import { useMemo } from 'react';
import contentData from '../data/content.json';
import contentDataCN from '../data/content-CN.json';
import { useLocale } from '../context/LocaleContext';
import { LOCALES } from '../context/LocaleContext';

const CONTENT_BY_LOCALE = {
  [LOCALES.EN]: contentData,
  [LOCALES.ZH_CN]: contentDataCN,
};

/**
 * Custom hook for managing content from JSON
 * Provides type-safe access to all content with helper functions
 * Content is selected based on current locale from LocaleContext
 */
export const useContent = () => {
  const { locale } = useLocale();
  const content = useMemo(
    () => CONTENT_BY_LOCALE[locale] || contentData,
    [locale]
  );

  // Helper functions for common content access patterns
  const getPageContent = (pageName) => {
    return content.pages[pageName] || {};
  };

  const getSiteInfo = () => {
    return content.site || {};
  };

  const getContactInfo = () => {
    return content.site.contact || {};
  };

  const getFooterInfo = () => {
    return content.site.footer || {};
  };

  // Specific page content getters
  const getHomeContent = () => getPageContent('home');
  const getAboutContent = () => getPageContent('about');
  const getServicesContent = () => getPageContent('services');
  const getWhyChooseUsContent = () => getPageContent('whyChooseUsPage');
  const getDisclaimerContent = () => getPageContent('disclaimer');
  const getPrivacyPolicyContent = () => getPageContent('privacyPolicy');
  const getTermsContent = () => getPageContent('termsAndConditions');
  const getLegalNoticeContent = () => content.legalNotice || {};
  const getContactContent = () => getPageContent('contact');

  // Simple page content getters
  const getPartnersContent = () => getPageContent('partners');
  const getConstructionContent = () => getPageContent('construction');
  const getIndustrialProductsContent = () => getPageContent('industrialProducts');
  const getElectricalProductsContent = () => getPageContent('electricalProducts');
  const getChemicalProductsContent = () => getPageContent('chemicalProducts');
  const getOtherProductsContent = () => getPageContent('otherProducts');

  // Solutions content getter
  const getSolutionsContent = () => getPageContent('solutions');

  return {
    // Raw content access
    content,

    // Helper functions
    getPageContent,
    getSiteInfo,
    getContactInfo,

    // Specific page content
    home: getHomeContent(),
    about: getAboutContent(),
    services: getServicesContent(),
    whyChooseUsPage: getWhyChooseUsContent(),
    disclaimer: getDisclaimerContent(),
    privacyPolicy: getPrivacyPolicyContent(),
    termsAndConditions: getTermsContent(),
    legalNotice: getLegalNoticeContent(),
    contact: getContactContent(),
    partners: getPartnersContent(),
    construction: getConstructionContent(),
    industrialProducts: getIndustrialProductsContent(),
    electricalProducts: getElectricalProductsContent(),
    chemicalProducts: getChemicalProductsContent(),
    otherProducts: getOtherProductsContent(),

    // Solutions content
    solutions: getSolutionsContent(),

    // Site-wide content
    site: getSiteInfo(),
    contactInfo: getContactInfo(),
    footerInfo: getFooterInfo()
  };
};

export default useContent;
